import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Globe, Shield, ShieldCheck } from 'lucide-react';
import { domainsApi, DomainCreate } from '../../lib/api/domains';
import { serversApi } from '../../lib/api/servers';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { toast } from '../../stores/toastStore';

const domainSchema = z.object({
  name: z
    .string()
    .min(1, 'Domain name is required')
    .max(255)
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/, 'Invalid domain format'),
  server_id: z.string().min(1, 'Server is required'),
  dns_target: z.string().optional(),
  ssl_enabled: z.boolean(),
  ssl_auto_renew: z.boolean(),
});

type DomainFormData = z.infer<typeof domainSchema>;

export function NewDomainPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: serversData } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list({ limit: 100 }),
  });

  const createDomain = useMutation({
    mutationFn: (data: DomainCreate) => domainsApi.create(data),
    onSuccess: (domain) => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      toast.success('Domain created successfully');
      navigate(`/domains/${domain.id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to create domain';
      toast.error(message);
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<DomainFormData>({
    resolver: zodResolver(domainSchema),
    defaultValues: {
      ssl_enabled: false,
      ssl_auto_renew: true,
    },
  });

  const sslEnabled = watch('ssl_enabled');

  const onSubmit = (data: DomainFormData) => {
    createDomain.mutate({
      name: data.name,
      server_id: data.server_id,
      dns_target: data.dns_target || undefined,
      ssl_enabled: data.ssl_enabled,
      ssl_auto_renew: data.ssl_auto_renew,
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/domains')}
        className="gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Domains
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Add New Domain
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Domain Name"
              placeholder="app.example.com"
              error={errors.name?.message}
              {...register('name')}
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Server
              </label>
              <select
                className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                {...register('server_id')}
              >
                <option value="">Select a server</option>
                {serversData?.items.map((server) => (
                  <option key={server.id} value={server.id}>
                    {server.name} ({server.host})
                  </option>
                ))}
              </select>
              {errors.server_id && (
                <p className="text-sm text-red-600">{errors.server_id.message}</p>
              )}
            </div>

            <Input
              label="DNS Target (IP Address)"
              placeholder="123.45.67.89"
              {...register('dns_target')}
            />
            <p className="text-xs text-gray-500 -mt-3">
              The IP address this domain should point to. Make sure DNS is configured.
            </p>

            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                SSL Configuration
              </h4>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  {...register('ssl_enabled')}
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    Enable SSL
                  </span>
                  <p className="text-xs text-gray-500">
                    Issue a free Let's Encrypt SSL certificate
                  </p>
                </div>
              </label>

              {sslEnabled && (
                <label className="flex items-center gap-3 cursor-pointer ml-7">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    {...register('ssl_auto_renew')}
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Auto-renew certificate
                    </span>
                    <p className="text-xs text-gray-500">
                      Automatically renew before expiration
                    </p>
                  </div>
                </label>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/domains')}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={createDomain.isPending}>
                {sslEnabled ? (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Create Domain with SSL
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4 mr-2" />
                    Create Domain
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h4 className="font-medium text-gray-900 mb-2">Before you continue</h4>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>
              Make sure your DNS is configured to point to the server's IP address
            </li>
            <li>
              SSL certificate issuance requires DNS to be properly configured
            </li>
            <li>
              If using Cloudflare or similar, disable proxy (gray cloud) during SSL
              setup
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
